import { useState, type FormEvent } from 'react';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import type { TwoStepCode } from '@london-life/contracts';

type Props = { submitLabel: string; allowRecovery?: boolean; onSubmit: (body: TwoStepCode) => Promise<void> };

// the one place an admin types a two-step code: six digits from the app, or a recovery code
export function CodeForm({ submitLabel, allowRecovery = true, onSubmit }: Props) {
  const [recovery, setRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailure(null);
    try {
      await onSubmit(recovery ? { recoveryCode: value.trim() } : { code: value.replace(/\s/g, '') });
    } catch (error) {
      setFailure(describeError(error));
      setValue('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="two-step-form" onSubmit={(event) => void submit(event)}>
      <div className="admin-field">
        <Label htmlFor="two-step-code">{recovery ? 'Recovery code' : '6-digit code from your authenticator app'}</Label>
        <Input
          id="two-step-code"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          autoComplete="one-time-code"
          inputMode={recovery ? 'text' : 'numeric'}
          pattern={recovery ? undefined : '[0-9 ]{6,7}'}
          maxLength={recovery ? 20 : 7}
          placeholder={recovery ? 'ABCDE-FGHJK' : '123 456'}
          autoFocus
          required
        />
      </div>
      {failure && <ErrorNotice error={failure} />}
      <Button type="submit" disabled={busy || !value}>
        {busy ? 'Checking…' : submitLabel}
      </Button>
      {allowRecovery && (
        <button
          type="button"
          className="two-step-switch"
          onClick={() => {
            setRecovery(!recovery);
            setValue('');
            setFailure(null);
          }}
        >
          {recovery ? 'Use the code from my app instead' : 'Lost your phone? Use a recovery code'}
        </button>
      )}
    </form>
  );
}
